export interface RadarAxis {
  readonly key: string;
  readonly label: string;
  /** Normalised 0..1, as measured by the engine. */
  readonly value: number;
}

/**
 * A behaviour radar, drawn as inline SVG.
 *
 * Two design rules inherited from `003.md` §6 and §「最后」:
 *
 *  1. **Every axis is measured, never authored.** The values come from
 *     `deriveTraits()` over real match counters. There is deliberately no
 *     "intelligence" axis, because we cannot measure intelligence and the doc is
 *     explicit that inventing a number like "IQ 92" is the failure mode to
 *     avoid. Behaviour is the evidence; the radar is only the summary.
 *  2. **Axis count is not fixed.** The caller passes whatever the engine
 *     measures, so adding a measured trait later needs no change here.
 *
 * The grid is 0.25 / 0.5 / 0.75 / 1.0 so a viewer can read approximate values
 * off the chart, and the 0.5 ring is emphasised because it is the neutral
 * midpoint the archetype classifier uses — above it is a defining trait, below
 * it is a weakness.
 */
export function RadarChart({
  axes,
  color,
  dim,
  size = 260,
  compare,
}: {
  axes: readonly RadarAxis[];
  color: string;
  dim: string;
  size?: number;
  /** Optional second series drawn underneath, for before/after comparison. */
  compare?: readonly RadarAxis[];
}) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.33;
  const rings = [0.25, 0.5, 0.75, 1];
  const n = axes.length;
  if (n < 3) return null;

  const pointAt = (index: number, value: number): [number, number] => {
    // Start at 12 o'clock and go clockwise.
    const angle = (index / n) * Math.PI * 2 - Math.PI / 2;
    return [cx + Math.cos(angle) * radius * value, cy + Math.sin(angle) * radius * value];
  };

  const polygon = (series: readonly RadarAxis[]): string =>
    series.map((axis, i) => pointAt(i, clamp01(axis.value)).join(",")).join(" ");

  return (
    <svg
      className="radar"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={axes.map((a) => `${a.label} ${Math.round(a.value * 100)}`).join(", ")}
    >
      {rings.map((ring) => (
        <polygon
          key={ring}
          points={axes.map((_, i) => pointAt(i, ring).join(",")).join(" ")}
          fill="none"
          stroke="var(--line)"
          strokeWidth={ring === 0.5 ? 1.4 : 1}
          strokeDasharray={ring === 0.5 ? undefined : "3 4"}
        />
      ))}

      {axes.map((_, i) => {
        const [x, y] = pointAt(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line)" strokeWidth={1} />;
      })}

      {compare ? (
        <polygon
          points={polygon(compare)}
          fill="none"
          stroke="var(--dimmer)"
          strokeWidth={1.5}
          strokeDasharray="5 4"
        />
      ) : null}

      <polygon points={polygon(axes)} fill={dim} fillOpacity={0.55} stroke={color} strokeWidth={2} />

      {axes.map((axis, i) => {
        const [x, y] = pointAt(i, clamp01(axis.value));
        return <circle key={axis.key} cx={x} cy={y} r={3} fill={color} />;
      })}

      {axes.map((axis, i) => {
        const [x, y] = pointAt(i, 1.26);
        const anchor = x > cx + 6 ? "start" : x < cx - 6 ? "end" : "middle";
        return (
          <g key={axis.key}>
            <text
              x={x}
              y={y}
              fill="var(--dim)"
              fontSize={10}
              textAnchor={anchor}
              dominantBaseline="middle"
            >
              {axis.label}
            </text>
            <text
              x={x}
              y={y + 12}
              fill={color}
              fontSize={10}
              fontWeight={700}
              textAnchor={anchor}
              dominantBaseline="middle"
            >
              {Math.round(axis.value * 100)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.min(1, Math.max(0, v));
}
